import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { getMyWorkspace, getMyWorkspaceEntitlements, listWorkspaceSites } from '../api'
import type { SaasApiError } from '../api'
import type { EntitlementsMap, SaasSite, SaasWorkspace } from '../types'
import { createLatestRequestGuard } from './latestRequestGuard'

interface WorkspaceState {
  workspace: SaasWorkspace | null
  sites: SaasSite[]
  /** Spec §7: a Workspace can have N sites; this phase's UI operates on
   * the first one (seed data only ever creates one) but never *assumes*
   * exactly one exists. */
  primarySite: SaasSite | null
  entitlements: EntitlementsMap
  loading: boolean
  error: SaasApiError | null
  reload: () => void
}

const WorkspaceCtx = createContext<WorkspaceState | null>(null)

export function WorkspaceProvider({ workspaceId, children }: { workspaceId: string; children: ReactNode }) {
  const [workspace, setWorkspace] = useState<SaasWorkspace | null>(null)
  const [sites, setSites] = useState<SaasSite[]>([])
  const [entitlements, setEntitlements] = useState<EntitlementsMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<SaasApiError | null>(null)
  // VistoeraTASK-00563 — a late response (success or 403) for a previous
  // workspace id must never overwrite the state of the current one.
  const guardRef = useRef(createLatestRequestGuard())

  const load = useCallback(() => {
    const guard = guardRef.current
    const token = guard.begin()
    setLoading(true)
    setError(null)
    Promise.all([getMyWorkspace(workspaceId), listWorkspaceSites(workspaceId), getMyWorkspaceEntitlements(workspaceId)])
      .then(([w, s, e]) => {
        if (!guard.isCurrent(token)) return
        setWorkspace(w.data)
        setSites(s.data)
        setEntitlements(e.data.entitlements)
      })
      .catch((err) => {
        if (!guard.isCurrent(token)) return
        setError(err)
      })
      .finally(() => {
        if (!guard.isCurrent(token)) return
        setLoading(false)
      })
  }, [workspaceId])

  useEffect(() => {
    load()
    const guard = guardRef.current
    return () => guard.invalidate()
  }, [load])

  const value: WorkspaceState = {
    workspace,
    sites,
    primarySite: sites[0] ?? null,
    entitlements,
    loading,
    error,
    reload: load,
  }

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>
}

export function useWorkspaceState() {
  const ctx = useContext(WorkspaceCtx)
  if (!ctx) throw new Error('useWorkspaceState must be used within WorkspaceProvider')
  return ctx
}
